import { MONEY_SCALE, compare, formatMoney, fromString } from '@aeris/money';
/**
 * As formas de pagamento do acerto do motoboy — F161, ampliada na F179.
 *
 * Fora do componente porque um módulo que mistura componente com funções faz o
 * Fast Refresh desistir do arquivo inteiro.
 *
 * ## O que muda com o cliente
 *
 * A lista era fechada: o que o motoboy pode trazer de volta na mão. O dono
 * mostrou o caso que faltava — *"faltou a opção vale e crédito, caso o cliente
 * tenha"* —, e as duas não são dinheiro que ele traz: são o saldo do cliente e
 * a conta dele na loja. Por isso elas dependem da ENTREGA, e não só do
 * cadastro de formas: aparecem quando aquele cliente tem com que pagar, e o
 * rótulo diz quanto, para o caixa não clicar e descobrir no erro.
 *
 * Quem decide de verdade é o servidor (`settleOnce`): saldo que não cobre,
 * limite estourado, bloqueio e atraso são recusados lá, com a frase. Isto aqui
 * é a ajuda de tela que evita a tentativa.
 */
/** As formas que um motoboy traz da rua. */
const STREET_KINDS = [
    'cash',
    'pix',
    'card_debit',
    'card_credit',
    'bank_transfer',
];
const covers = (available, outstanding) => {
    const has = fromString(available === '' ? '0' : available, MONEY_SCALE);
    const owes = fromString(outstanding === '' ? '0' : outstanding, MONEY_SCALE);
    return owes.units > 0n && compare(has, owes) >= 0;
};
const sorted = (methods) => [...methods]
    .filter((method) => STREET_KINDS.includes(method.kind))
    .sort((a, b) => STREET_KINDS.indexOf(a.kind) - STREET_KINDS.indexOf(b.kind));
export const streetMethods = (methods) => sorted(methods);
/**
 * As formas desta entrega: as da rua, mais o que o cliente tem.
 *
 * O vale e o crediário entram no FIM porque a esmagadora maioria das entregas
 * é dinheiro, e o primeiro botão tem de continuar sendo o mais clicado.
 *
 * Só quando cobrem o que falta: um saldo de R$ 10,00 num pedido de R$ 50,00
 * seria um botão que o servidor recusa. Baixa parcial com vale existe pelo
 * "Dividir", onde a pessoa escreve o valor.
 */
export const streetMethodsFor = (methods, purse) => [
    ...sorted(methods).map((method) => ({ method, label: method.name })),
    ...methods
        .filter((method) => method.kind === 'voucher' &&
        covers(purse.voucherBalance, purse.outstanding))
        .map((method) => ({
        method,
        label: `${method.name} (${formatMoney(fromString(purse.voucherBalance, MONEY_SCALE))})`,
    })),
    ...methods
        .filter((method) => method.kind === 'customer_credit' &&
        covers(purse.creditAvailable, purse.outstanding))
        .map((method) => ({
        method,
        label: `${method.name} (livre ${formatMoney(fromString(purse.creditAvailable, MONEY_SCALE))})`,
    })),
];
const positive = (value) => fromString(value === '' ? '0' : value, MONEY_SCALE).units > 0n;
/**
 * As formas de uma PARTE do "Dividir" — F211 (o vale da CELUKECE).
 *
 * O botão direto paga o pedido inteiro com uma forma só, e por isso o vale só
 * vira botão quando cobre tudo. O aviso dizia "não cobre o que falta — use
 * Dividir"… e o Dividir usava a MESMA lista, sem o vale. Pedido de R$ 245,00
 * com vale de R$ 229,00: o caixa não achou o vale em lugar nenhum e lançou os
 * R$ 229,00 no cartão de débito, com o saldo intacto.
 *
 * Numa parte, o vale entra com QUALQUER saldo — a pessoa escreve quanto usa, e
 * o servidor recusa o que passar do saldo (`finance.ts`, F179). O mesmo para o
 * limite livre do crediário.
 */
export const splitMethodsFor = (methods, purse) => [
    ...sorted(methods).map((method) => ({ method, label: method.name })),
    ...methods
        .filter((method) => method.kind === 'voucher' && positive(purse.voucherBalance))
        .map((method) => ({
        method,
        label: `${method.name} (${formatMoney(fromString(purse.voucherBalance, MONEY_SCALE))})`,
    })),
    ...methods
        .filter((method) => method.kind === 'customer_credit' && positive(purse.creditAvailable))
        .map((method) => ({
        method,
        label: `${method.name} (livre ${formatMoney(fromString(purse.creditAvailable, MONEY_SCALE))})`,
    })),
];
//# sourceMappingURL=street-methods.js.map